import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { MagicBox, useIsMobile } from "@brg/ui";
import { Tag } from "@brg/api";
import {
  TagsSearchModalContainer,
  TagsSearchSelection,
  TagsSelectedTitle,
  TagsSearchScrollWrapper,
  TabsContainer,
  MobileSubmitButton,
} from "./styles.mobile";
import { BTag, BTagsGrid } from "../../../core/components";
import { bTag, modalControllerActions } from "../../../core";
import { CategoryHeader } from "./CategoryHeader";
import { useDispatch } from "react-redux";
import { TagsActions } from "shared/reducers/Tags";
import { EmptyTextWrapper } from "./FoundTags";
import { LoaderSpinner } from "../artzLegalsModal/LoaderAnimation";
import { BTagsGridNew } from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { SwitcherItem } from "../../components/ChannelsInfo/styles";
import { isRequired } from "./support/isRequired";
import { bProfile } from "../../../core/utils/bProfile";
import { useProfile } from "@brg/auth";
import { TagsSearchInputComponent } from "./components/TagsSearchInputComponent";
import { TagsSearchResults } from "./components/TagsSearchResults";
import { SelectTagsModalTabsModeProps } from "./types/selectMobileTags";
import { debounceEvent } from "shared/utils/debounce";

const AlignedBTagsGrid = styled(MagicBox)`
  ${BTagsGrid} {
    // div:first-child {
    //   margin-left: 90px;
    // }
  }
`;

export const SelectTagsModalTabsMode: FC<SelectTagsModalTabsModeProps> = ({
  headerTag,
  searchText,
  load,
  displayedTags,
  filteredTags,
  getGrouping,
  onTagSelect,
  shouldVisibleEducationCategory,
  isMobileMenu,
  viewAllTags,
  isEdit,
  onMoreClick,
  showAllTagsInGroup,
  isTagEditor,
  editableTags = [],
  innerValue,
  mode,
  t,
  theme,
  mainSearchRef,
  tagsScrollRef,
  onChange,
}) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const profile = useProfile();
  const [activeTab, setActiveTab] = useState<'tags' | 'selected'>('tags');
  const [searchInputValue, setSearchInputValue] = useState('');

  const handleApplySearchText = useCallback((e: any) => {
    const str = e.target.value.trim();
    if (str && str.length > 1) {
      dispatch(TagsActions.setSearchText(str));
    }
    if (str === '') {
      dispatch(TagsActions.setSearchText(''));
    }
  }, [dispatch]);

  const debouncedApplySearchText = useMemo(
    () => debounceEvent(handleApplySearchText, 1000),
    [handleApplySearchText],
  );

  const handleSearchInputChange = useCallback((e: any) => {
    setSearchInputValue(e.target.value);
    debouncedApplySearchText(e);
  }, [debouncedApplySearchText]);

  const handleClearSearchClick = useCallback(() => {
    setSearchInputValue('');
    if (mainSearchRef?.current) {
      mainSearchRef.current.value = '';
      mainSearchRef.current.focus?.();
    }
    handleApplySearchText({ target: { value: '' } });
  }, [handleApplySearchText, mainSearchRef]);

  useEffect(() => {
    if (searchText !== undefined) {
      setSearchInputValue(searchText);
    }
  }, [searchText]);

  const handleTabChange = useCallback((tab: 'tags' | 'selected') => {
    setActiveTab(tab);
    if (tagsScrollRef.current) {
      tagsScrollRef.current.scrollTo(0, 0);
    }
  }, [tagsScrollRef]);

  const onSaveTagsByClick = useCallback(() => {
    onChange && onChange(innerValue);
    dispatch(modalControllerActions.hideModal({}));
  }, [onChange, innerValue, dispatch]);

  const SelectedTagsComponent = useMemo(
    () => (
      <TagsSearchSelection>
        {!!innerValue.length && (
          <AlignedBTagsGrid offsetTop={0}>
            <BTagsGridNew>
              {innerValue.map((v: any) => (
                  <BTag
                    tag={v}
                    removable={true}
                    key={v.tagId}
                    educational={isRequired(v)}
                    organization={v?.isTeamTag}
                    showRemoveIcon={
                      !(
                        isTagEditor &&
                        !editableTags.includes(v.tagId) &&
                        !(bProfile.isModer(profile) || bProfile.isSuperModer(profile))
                      )
                    }
                    // @ts-ignore
                    disabled={
                      isTagEditor &&
                      !editableTags.includes(v.tagId) &&
                      !(bProfile.isModer(profile) || bProfile.isSuperModer(profile))
                    }
                    onIconClick={() => onTagSelect(v)}
                  >
                    {bTag.title(v)}
                  </BTag>))}
            </BTagsGridNew>
          </AlignedBTagsGrid>
        )}
      </TagsSearchSelection>
    ),
    [innerValue, mode, isTagEditor, t, editableTags, profile, onTagSelect, isMobileMenu]
  );

  return (
    <>
      <TabsContainer>
        <SwitcherItem isActive={activeTab === 'tags'} onClick={() => handleTabChange('tags')}>
          {t('tags_modal.tags_tab')}
        </SwitcherItem>
        <SwitcherItem
          isActive={activeTab === 'selected'}
          onClick={() => handleTabChange('selected')}
        >
          {t('tags_modal.selected_tab')}
        </SwitcherItem>
      </TabsContainer>
      {activeTab === 'tags' && (
        <TagsSearchScrollWrapper ref={tagsScrollRef}>
          <TagsSearchModalContainer isMobileMenu={isMobileMenu}>
            {headerTag && <CategoryHeader headerTag={headerTag} />}
            <TagsSearchInputComponent
              mainSearchRef={mainSearchRef}
              onInputText={handleApplySearchText}
              value={searchInputValue}
              onChange={handleSearchInputChange}
              onClear={handleClearSearchClick}
              isMobileMenu={isMobileMenu}
              showCaption={isMobileMenu}
            />
            {load && (
              <EmptyTextWrapper
                directionMobile="column"
                gy={24}
                width="100vw"
                align="center"
                justify="center"
                isMobileMenu={isMobileMenu}
              >
                <LoaderSpinner
                  color={theme?.selectContextModal?.loaderText}
                  text={t('project_edit.modal.load_msg_tag')}
                />
              </EmptyTextWrapper>
            )}
            <TagsSearchResults
              displayedTags={displayedTags}
              filteredTags={filteredTags}
              getGrouping={getGrouping}
              onTagSelect={onTagSelect}
              shouldVisibleEducationCategory={shouldVisibleEducationCategory}
              isMobileMenu={isMobileMenu}
              viewAllTags={viewAllTags}
              load={load}
              isEdit={isEdit}
              onMoreClick={onMoreClick}
              showAllTagsInGroup={showAllTagsInGroup}
              isTagEditor={isTagEditor}
              t={t}
              activeTab={activeTab}
            />
          </TagsSearchModalContainer>
        </TagsSearchScrollWrapper>
      )}
      {activeTab === 'selected' && (
        <TagsSearchScrollWrapper ref={tagsScrollRef}>
          <TagsSearchModalContainer isMobileMenu={isMobileMenu}>
            {SelectedTagsComponent}
          </TagsSearchModalContainer>
        </TagsSearchScrollWrapper>
      )}
      <MobileSubmitButton
        className="select-tags-modal--save-button"
        onClick={onSaveTagsByClick}
      >
        {t('save_tag')}
      </MobileSubmitButton>
    </>
  );
};
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { MagicBox, useIsMobile } from "@brg/ui";
import { Tag } from "@brg/api";
import {
  TagsSearchModalContainer,
  TagsSearchSelection,
  TagsSelectedTitle,
  TagsSearchScrollWrapper,
  TabsContainer,
  MobileSubmitButton,
} from "./styles.mobile";
import { BTag, BTagsGrid } from "../../../core/components";
import { bTag, modalControllerActions } from "../../../core";
import { CategoryHeader } from "./CategoryHeader";
import { useDispatch } from "react-redux";
import { TagsActions } from "shared/reducers/Tags";
import { EmptyTextWrapper } from "./FoundTags";
import { LoaderSpinner } from "../artzLegalsModal/LoaderAnimation";
import { BTagsGridNew } from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { SwitcherItem } from "../../components/ChannelsInfo/styles";
import { isRequired } from "./support/isRequired";
import { bProfile } from "../../../core/utils/bProfile";
import { useProfile } from "@brg/auth";
import { TagsSearchInputComponent } from "./components/TagsSearchInputComponent";
import { TagsSearchResults } from "./components/TagsSearchResults";
import { SelectTagsModalTabsModeProps } from "./types/selectMobileTags";
import { debounceEvent } from "shared/utils/debounce";

const AlignedBTagsGrid = styled(MagicBox)`
  ${BTagsGrid} {
    // div:first-child {
    //   margin-left: 90px;
    // }
  }
`;

export const SelectTagsModalTabsMode: FC<SelectTagsModalTabsModeProps> = ({
  headerTag,
  searchText,
  load,
  displayedTags,
  filteredTags,
  getGrouping,
  onTagSelect,
  shouldVisibleEducationCategory,
  isMobileMenu,
  viewAllTags,
  isEdit,
  onMoreClick,
  showAllTagsInGroup,
  isTagEditor,
  editableTags = [],
  innerValue,
  mode,
  t,
  theme,
  mainSearchRef,
  tagsScrollRef,
  onChange,
}) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const profile = useProfile();
  const [activeTab, setActiveTab] = useState<'tags' | 'selected'>('tags');
  const [searchInputValue, setSearchInputValue] = useState('');

  const handleApplySearchText = useCallback((e: any) => {
    const str = e.target.value.trim();
    if (str && str.length > 1) {
      dispatch(TagsActions.setSearchText(str));
    }
    if (str === '') {
      dispatch(TagsActions.setSearchText(''));
    }
  }, [dispatch]);

  const debouncedApplySearchText = useMemo(
    () => debounceEvent(handleApplySearchText, 1000),
    [handleApplySearchText],
  );

  const handleSearchInputChange = useCallback((e: any) => {
    setSearchInputValue(e.target.value);
    debouncedApplySearchText(e);
  }, [debouncedApplySearchText]);

  const handleClearSearchClick = useCallback(() => {
    setSearchInputValue('');
    if (mainSearchRef?.current) {
      mainSearchRef.current.value = '';
      mainSearchRef.current.focus?.();
    }
    handleApplySearchText({ target: { value: '' } });
  }, [handleApplySearchText, mainSearchRef]);

  useEffect(() => {
    if (searchText !== undefined) {
      setSearchInputValue(searchText);
    }
  }, [searchText]);

  const handleTabChange = useCallback((tab: 'tags' | 'selected') => {
    setActiveTab(tab);
    if (tagsScrollRef.current) {
      tagsScrollRef.current.scrollTo(0, 0);
    }
  }, [tagsScrollRef]);

  const onSaveTagsByClick = useCallback(() => {
    onChange && onChange(innerValue);
    dispatch(modalControllerActions.hideModal({}));
  }, [onChange, innerValue, dispatch]);

  const SelectedTagsComponent = useMemo(
    () => (
      <TagsSearchSelection>
        {!!innerValue.length && (
          <AlignedBTagsGrid offsetTop={0}>
            <BTagsGridNew>
              {innerValue.map((v: any) => (
                  <BTag
                    tag={v}
                    removable={true}
                    key={v.tagId}
                    educational={isRequired(v)}
                    organization={v?.isTeamTag}
                    showRemoveIcon={
                      !(
                        isTagEditor &&
                        !editableTags.includes(v.tagId) &&
                        !(bProfile.isModer(profile) || bProfile.isSuperModer(profile))
                      )
                    }
                    // @ts-ignore
                    disabled={
                      isTagEditor &&
                      !editableTags.includes(v.tagId) &&
                      !(bProfile.isModer(profile) || bProfile.isSuperModer(profile))
                    }
                    onIconClick={() => onTagSelect(v)}
                  >
                    {bTag.title(v)}
                  </BTag>))}
            </BTagsGridNew>
          </AlignedBTagsGrid>
        )}
      </TagsSearchSelection>
    ),
    [innerValue, mode, isTagEditor, t, editableTags, profile, onTagSelect, isMobileMenu]
  );

  return (
    <>
      <TabsContainer>
        <SwitcherItem isActive={activeTab === 'tags'} onClick={() => handleTabChange('tags')}>
          {t('tags_modal.tags_tab')}
        </SwitcherItem>
        <SwitcherItem
          isActive={activeTab === 'selected'}
          onClick={() => handleTabChange('selected')}
        >
          {t('tags_modal.selected_tab')}
        </SwitcherItem>
      </TabsContainer>
      {activeTab === 'tags' && (
        <TagsSearchScrollWrapper ref={tagsScrollRef}>
          <TagsSearchModalContainer isMobileMenu={isMobileMenu}>
            {headerTag && <CategoryHeader headerTag={headerTag} />}
            <TagsSearchInputComponent
              mainSearchRef={mainSearchRef}
              onInputText={handleApplySearchText}
              value={searchInputValue}
              onChange={handleSearchInputChange}
              onClear={handleClearSearchClick}
              isMobileMenu={isMobileMenu}
              showCaption={isMobileMenu}
            />
            {load && (
              <EmptyTextWrapper
                directionMobile="column"
                gy={24}
                width="100vw"
                align="center"
                justify="center"
                isMobileMenu={isMobileMenu}
              >
                <LoaderSpinner
                  color={theme?.selectContextModal?.loaderText}
                  text={t('project_edit.modal.load_msg_tag')}
                />
              </EmptyTextWrapper>
            )}
            <TagsSearchResults
              displayedTags={displayedTags}
              filteredTags={filteredTags}
              getGrouping={getGrouping}
              onTagSelect={onTagSelect}
              shouldVisibleEducationCategory={shouldVisibleEducationCategory}
              isMobileMenu={isMobileMenu}
              viewAllTags={viewAllTags}
              load={load}
              isEdit={isEdit}
              onMoreClick={onMoreClick}
              showAllTagsInGroup={showAllTagsInGroup}
              isTagEditor={isTagEditor}
              t={t}
              activeTab={activeTab}
            />
          </TagsSearchModalContainer>
        </TagsSearchScrollWrapper>
      )}
      {activeTab === 'selected' && (
        <TagsSearchScrollWrapper ref={tagsScrollRef}>
          <TagsSearchModalContainer isMobileMenu={isMobileMenu}>
            {SelectedTagsComponent}
          </TagsSearchModalContainer>
        </TagsSearchScrollWrapper>
      )}
      <MobileSubmitButton
        className="select-tags-modal--save-button"
        onClick={onSaveTagsByClick}
      >
        {t('save_tag')}
      </MobileSubmitButton>
    </>
  );
};
